/**
 * PATH: packages/email-hub/src/feature/components/CategoryChip.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-CATEGORYCHIP-001
 * PURPOSE: Canonical body of this email component. Lifted from
 *          apps/<tenant>/src/features/email/components/CategoryChip.tsx
 *          on K3a (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale (BUG-0032 was
 *          the most recent example). One canonical body, both
 *          tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/components/CategoryChip.tsx:
 *     thin re-export shim that points back here.
 */

import { useState, useRef, useEffect } from 'react'
import { useCloseOnOutsidePointer } from '@quotech/ui'
import { ChevronDown, EyeOff, Lock, Plus, RotateCcw, Tag, Users } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import { cn } from '../../_internal/lib/utils'
import { useToast } from '../../_internal/components/Toast'
import { AnchoredPopover } from '../../_internal/components/AnchoredPopover'

import { useEmailActions } from '../hooks/useEmails'
import {
  createOrgCategory,
  createPersonalCategory,
  nestLabelOptions,
  useOrgCustomCategories,
  usePersonalCategories,
} from '../hooks/useEmailLabelPrefs'
import {
  isCategoryMuted,
  muteCategory,
  unmuteCategory,
  useMutedCategories,
} from '../stores/mutedCategoriesStore'
import { CATEGORY_PICKER_OPTIONS } from '@quotech/email-hub/feature/constants/categories'
import { getCategoryConfig } from '../lib/categoryConfig'

import { CategoryPill } from '@quotech/ui'

interface CategoryChipProps {
  emailId: string
  category: string | undefined
  categoryLocked: boolean | undefined
  /** Tailwind classes for the pill (`bg-orange-100 text-orange-700 ...`) */
  pillClass: string
  /** Lucide icon to render inside the pill (e.g. Receipt for "bill"). */
  icon: LucideIcon
  /** Display label ("Supplier Bill", "Quote Request", ...). */
  label: string
  /** Compact variant — smaller padding/font, used in the email list. */
  size?: 'sm' | 'md'
  /** When set, render the pill with a solid colour dot (the
   *  Monday/ClickUp "tag" look) instead of the category glyph. Pass the
   *  category hue (`getCategoryConfig(...).hex`). Opt-in per surface. */
  dotColor?: string
  /** Called after a successful category change so the parent can refetch. */
  onChanged?: () => void
}

export function CategoryChip({
  emailId,
  category,
  categoryLocked,
  pillClass,
  icon,
  label,
  size = 'sm',
  dotColor,
  onChanged,
}: CategoryChipProps) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const { setCategory, bulkResetCategory } = useEmailActions()
  const { toast } = useToast()
  const [busy, setBusy] = useState(false)
  // Optimistic category override (P2 "feels broken" sweep). The pill's
  // pillClass / icon / label are computed by the PARENT from `category`, so
  // until the post-change refetch lands the chip kept showing the OLD pill.
  // We stash the just-applied key here and resolve the FULL config from it
  // (getCategoryConfig handles org + personal keys, pixel-identical to the
  // parent), so the new pill paints in the same frame as the click. Cleared
  // the instant the `category` prop catches up (or on failure).
  const [pendingKey, setPendingKey] = useState<string | null>(null)
  useEffect(() => {
    setPendingKey(null)
  }, [category])
  // Inline "create a new label" affordance. `creating` carries the tier:
  // 'personal' (operator 2026-06-21: "I can't create new categories in
  // Personal ... or otherwise") or 'org' (operator 2026-07-03: "only able
  // to create labels in Personal but not in system").
  const [creating, setCreating] = useState<null | 'personal' | 'org'>(null)
  const [newLabel, setNewLabel] = useState('')
  // Viewer's PRIVATE label tier — offered alongside the org categories so an
  // email can be filed "Personal" (operator: "where can I label it personal").
  const personalCategories = usePersonalCategories()
  // Org-shared CUSTOM labels — rendered inside the team section.
  const orgCustomCategories = useOrgCustomCategories()
  const customCategories = [...personalCategories, ...orgCustomCategories]
  // Muted tier — powers the "Hide from inbox" action at the picker foot.
  const mutedCategories = useMutedCategories()

  // BUG-2026-07-17-TAG-PICKER-CLIPPED — the picker is body-portalled
  // (see AnchoredPopover), so click-away must also treat clicks INSIDE
  // the portal as inside.
  const popoverRef = useRef<HTMLDivElement | null>(null)

  // Reused from: packages/ui/src/hooks/useCloseOnOutsidePointer.ts (useCloseOnOutsidePointer)
  useCloseOnOutsidePointer(open, () => setOpen(false), [containerRef, popoverRef])

  // Closing the picker discards any half-typed new label so it never
  // reappears pre-filled the next time the picker opens.
  useEffect(() => {
    if (!open) {
      setCreating(null)
      setNewLabel('')
    }
  }, [open])

  const apply = async (key: string, optionLabel: string) => {
    setBusy(true)
    setPendingKey(key) // optimistic: repaint the pill now
    try {
      await setCategory(emailId, key)
      toast(`Pinned as “${optionLabel}”`, 'success')
      onChanged?.()
    } catch (err) {
      setPendingKey(null) // revert to the parent's category on failure
      toast(`Could not set category: ${(err as Error).message || 'error'}`, 'error')
    } finally {
      setBusy(false)
      setOpen(false)
    }
  }

  const reset = async () => {
    setBusy(true)
    setPendingKey('uncategorized') // optimistic: drop back to "Categorise ▾"
    try {
      await bulkResetCategory([emailId])
      toast('Cleared override - AI will re-classify', 'success')
      onChanged?.()
    } catch (err) {
      setPendingKey(null)
      toast(`Could not reset: ${(err as Error).message || 'error'}`, 'error')
    } finally {
      setBusy(false)
      setOpen(false)
    }
  }

  // Create a brand-new label (private personal OR org-shared team, per the
  // active `creating` tier) AND immediately file this email under it — one
  // motion from the picker, no detour to Settings.
  const createAndApply = async () => {
    const label = newLabel.trim()
    if (!label || !creating) return
    setBusy(true)
    try {
      if (creating === 'org') {
        const result = await createOrgCategory(label)
        if (result.kind === 'proposed') {
          toast(
            result.message ||
              'Org label proposed for approval — it will appear after accept.',
            'success',
          )
          setNewLabel('')
          setCreating(null)
          setOpen(false)
          return
        }
        setPendingKey(result.category.slug)
        await setCategory(emailId, result.category.slug)
        toast(`Created & pinned “${result.category.label}”`, 'success')
      } else {
        const created = await createPersonalCategory(label)
        setPendingKey(created.slug)
        await setCategory(emailId, created.slug)
        toast(`Created & pinned “${created.label}”`, 'success')
      }
      onChanged?.()
      setNewLabel('')
      setCreating(null)
      setOpen(false)
    } catch (err) {
      setPendingKey(null)
      toast(`Could not create label: ${(err as Error).message || 'error'}`, 'error')
    } finally {
      setBusy(false)
    }
  }

  // An email with no AI/manual category is a dead end unless we offer a way
  // IN. Operator (2026-06-17): "[Fireflies] is an app I subscribed to yet it
  // is uncategorised … there should be an option somewhere." Right-click did
  // expose "Set category", but that's invisible on touch — so for unset rows
  // we render an explicit, tappable "Categorise ▾" affordance that opens the
  // very same picker (org categories + the viewer's Personal labels).
  // Effective (optimistic-aware) display values. When a change is pending we
  // resolve the full pill config from the just-applied key so the chip repaints
  // immediately; otherwise we use the parent-computed props verbatim.
  const eff = pendingKey ? getCategoryConfig(pendingKey, customCategories) : null
  const effCategory = pendingKey ?? category
  const effPill = eff ? eff.pill : pillClass
  const effIcon = eff ? eff.icon : icon
  const effLabel = eff ? eff.label : label
  // Only override the dot colour when the surface opted into dot-mode already,
  // so a pending change never flips a glyph pill into a dot pill mid-flight.
  const effDot = eff && dotColor != null ? eff.hex : dotColor
  const effLocked = pendingKey ? true : categoryLocked
  const isUnset = !effCategory || effCategory === 'uncategorized'

  // Shared inline "name the new label" row — rendered inside whichever
  // tier section (team / personal) opened it.
  const renderCreateRow = (tier: 'personal' | 'org') => (
    <div className="flex items-center gap-1 px-2 py-1">
      <input
        autoFocus
        data-testid={tier === 'org' ? 'new-org-label-input' : 'new-personal-label-input'}
        value={newLabel}
        disabled={busy}
        maxLength={80}
        placeholder={tier === 'org' ? 'New team label name' : 'New label name'}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setNewLabel(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            void createAndApply()
          } else if (e.key === 'Escape') {
            e.preventDefault()
            setCreating(null)
            setNewLabel('')
          }
        }}
        className="flex-1 min-w-0 rounded border border-border bg-background px-1.5 py-0.5 text-[11px] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      />
      <button
        type="button"
        data-testid={tier === 'org' ? 'new-org-label-add' : 'new-personal-label-add'}
        disabled={busy || !newLabel.trim()}
        onClick={() => void createAndApply()}
        className={cn(
          'rounded px-2 py-0.5 text-[10px] font-medium text-white disabled:opacity-50',
          tier === 'org' ? 'bg-cyan-700 hover:bg-cyan-800' : 'bg-indigo-600 hover:bg-indigo-700',
        )}
      >
        Add
      </button>
    </div>
  )

  return (
    <div ref={containerRef} className="relative inline-flex">
      {isUnset ? (
        <button
          type="button"
          disabled={busy}
          data-testid="categorise-trigger"
          title="Categorise this email"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={(e) => {
            e.stopPropagation()
            setOpen((o) => !o)
          }}
          className={cn(
            'inline-flex items-center gap-1 rounded-full border border-dashed border-border',
            'px-2 py-0.5 text-[10px] font-medium text-muted-foreground',
            'hover:border-indigo-300 hover:bg-indigo-500/10 hover:text-indigo-600',
            'dark:hover:text-indigo-300 transition-colors',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            busy && 'opacity-60 cursor-not-allowed',
          )}
        >
          <Tag className="h-2.5 w-2.5 shrink-0" aria-hidden />
          <span>Categorise</span>
          <ChevronDown className="h-2.5 w-2.5 shrink-0 text-muted-foreground/60" aria-hidden />
        </button>
      ) : (
        <CategoryPill
          pillClass={effPill}
          icon={effIcon}
          label={effLabel}
          locked={effLocked}
          size={size}
          dotColor={effDot}
          disabled={busy}
          onClick={(e) => {
            e.stopPropagation()
            setOpen((o) => !o)
          }}
        />
      )}

      {/* Body-portalled so the Signal Strip's overflow clipping (or any
          other ancestor) can never truncate the picker
          (BUG-2026-07-17-TAG-PICKER-CLIPPED). The `open &&` guard keeps
          the picker tree lazily built — this chip renders per list row. */}
      {open && (
      <AnchoredPopover
        anchorRef={containerRef}
        open={open}
        popoverRef={popoverRef}
        role="menu"
        className="min-w-[160px] rounded-md border border-border bg-card shadow-lg p-1 overflow-y-auto"
      >
          {CATEGORY_PICKER_OPTIONS.map(opt => (
            <button
              key={opt.key}
              type="button"
              disabled={busy}
              onClick={() => void apply(opt.key, opt.label)}
              className={cn(
                'flex items-center gap-2 w-full px-2.5 py-1 text-[11px] text-left rounded transition-colors',
                'hover:bg-accent disabled:opacity-50',
                effCategory === opt.key && 'bg-accent/50 font-semibold',
              )}
            >
              <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: opt.dotColor }} />
              <span className="flex-1 truncate">{opt.label}</span>
              {effCategory === opt.key && effLocked && (
                <Lock className="h-2.5 w-2.5 text-muted-foreground" />
              )}
            </button>
          ))}
          {/* Team tier — org-shared CUSTOM labels every member sees, plus
              an inline "New team label" (operator 2026-07-03: "only able
              to create labels in Personal but not in system"). Folders-style
              order: children indented under their parent (2026-07-06). */}
          {nestLabelOptions(orgCustomCategories).map((opt) => (
            <button
              key={opt.slug}
              type="button"
              disabled={busy}
              onClick={() => void apply(opt.slug, opt.label)}
              className={cn(
                'flex items-center gap-2 w-full px-2.5 py-1 text-[11px] text-left rounded transition-colors',
                'hover:bg-accent disabled:opacity-50',
                effCategory === opt.slug && 'bg-accent/50 font-semibold',
                opt.depth > 0 && 'pl-6',
              )}
            >
              <span
                className="h-2 w-2 rounded-full shrink-0"
                style={{ backgroundColor: opt.color || '#0891b2' }}
              />
              <span className="flex-1 truncate">{opt.label}</span>
              <Users className="h-2.5 w-2.5 text-muted-foreground/60" />
              {effCategory === opt.slug && effLocked && (
                <Lock className="h-2.5 w-2.5 text-muted-foreground" />
              )}
            </button>
          ))}
          {creating === 'org' ? (
            renderCreateRow('org')
          ) : (
            <button
              type="button"
              data-testid="new-org-label-trigger"
              disabled={busy}
              onClick={() => {
                setCreating('org')
                setNewLabel('')
              }}
              className={cn(
                'flex items-center gap-2 w-full px-2.5 py-1 text-[11px] text-left rounded transition-colors',
                'text-cyan-700 dark:text-cyan-300 hover:bg-accent disabled:opacity-50',
              )}
            >
              <Plus className="h-2.5 w-2.5 shrink-0" />
              <span>New team label</span>
            </button>
          )}
          {/* Personal tier — the viewer's own private labels, plus an inline
              "New label" so a personal category can be born right here
              (operator 2026-06-21: "I can't create new categories in Personal
              … or otherwise"). Always rendered so the create affordance exists
              even before the first personal label. */}
          <div className="my-1 border-t border-border" />
          <p className="px-2.5 pb-0.5 pt-1 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground/70">
            Personal
          </p>
          {nestLabelOptions(personalCategories).map((opt) => (
            <button
              key={opt.slug}
              type="button"
              disabled={busy}
              onClick={() => void apply(opt.slug, opt.label)}
              className={cn(
                'flex items-center gap-2 w-full px-2.5 py-1 text-[11px] text-left rounded transition-colors',
                'hover:bg-accent disabled:opacity-50',
                effCategory === opt.slug && 'bg-accent/50 font-semibold',
                opt.depth > 0 && 'pl-6',
              )}
            >
              <span
                className="h-2 w-2 rounded-full shrink-0"
                style={{ backgroundColor: opt.color || '#6366f1' }}
              />
              <span className="flex-1 truncate">{opt.label}</span>
              {effCategory === opt.slug && effLocked && (
                <Lock className="h-2.5 w-2.5 text-muted-foreground" />
              )}
            </button>
          ))}
          {creating === 'personal' ? (
            renderCreateRow('personal')
          ) : (
            <button
              type="button"
              data-testid="new-personal-label-trigger"
              disabled={busy}
              onClick={() => {
                setCreating('personal')
                setNewLabel('')
              }}
              className={cn(
                'flex items-center gap-2 w-full px-2.5 py-1 text-[11px] text-left rounded transition-colors',
                'text-indigo-600 dark:text-indigo-300 hover:bg-accent disabled:opacity-50',
              )}
            >
              <Plus className="h-2.5 w-2.5 shrink-0" />
              <span>New label</span>
            </button>
          )}
          <div className="my-1 border-t border-border" />
          {/* Route this kind of mail out of the default inbox (operator
              2026-07-03: "an email like this I don't really always want to
              see... it should go somewhere"). The category chip in the
              filter bar still reveals the hidden rows on demand. */}
          {!isUnset && effCategory && (
            (() => {
              const muted = isCategoryMuted(effCategory)
              void mutedCategories // subscribe: repaint when the muted set changes
              return (
                <button
                  type="button"
                  data-testid="mute-category-toggle"
                  disabled={busy}
                  onClick={() => {
                    if (muted) {
                      unmuteCategory(effCategory)
                      toast(`"${effLabel}" emails will show in the inbox again`, 'success')
                    } else {
                      muteCategory(effCategory)
                      toast(`"${effLabel}" emails are now hidden from the inbox. Use the "${effLabel}" filter chip to see them.`, 'success')
                    }
                    setOpen(false)
                  }}
                  className={cn(
                    'flex items-center gap-2 w-full px-2.5 py-1 text-[11px] text-left rounded transition-colors',
                    'hover:bg-accent text-muted-foreground disabled:opacity-40',
                  )}
                >
                  <EyeOff className="h-2.5 w-2.5 shrink-0" />
                  <span>{muted ? `Show "${effLabel}" in inbox` : `Hide "${effLabel}" from inbox`}</span>
                </button>
              )
            })()
          )}
          <button
            type="button"
            disabled={busy || !effLocked}
            onClick={() => void reset()}
            className={cn(
              'flex items-center gap-2 w-full px-2.5 py-1 text-[11px] text-left rounded transition-colors',
              'hover:bg-accent text-muted-foreground disabled:opacity-40',
            )}
          >
            <RotateCcw className="h-2.5 w-2.5 shrink-0" />
            <span>Reset to AI</span>
          </button>
      </AnchoredPopover>
      )}
    </div>
  )
}
